<template>
	<section class="category-manage">
		<p class="cm-lead">
			当前产品分类：<strong>{{ rootName || '—' }}</strong>
			。只维护该产品分类下的商品分类，可多级添加。
		</p>
		<div class="cm-toolbar">
			<el-input
				v-model="search"
				class="cm-search"
				clearable
				prefix-icon="el-icon-search"
				placeholder="搜索商品分类"
				@keyup.enter.native="onSearch"
				@clear="onSearch" />
			<div class="cm-toolbar__ops">
				<el-button size="mini" @click="setExpandAll(true)">全部展开</el-button>
				<el-button size="mini" @click="setExpandAll(false)">全部收起</el-button>
				<el-button v-if="canAdd" type="primary" icon="el-icon-plus" size="small" @click="openCreate(parentId)">添加商品分类</el-button>
			</div>
		</div>

		<el-table
			ref="cateTable"
			v-loading="loading"
			:data="treeList"
			stripe
			row-key="id"
			:tree-props="{ children: 'children' }"
			:default-expand-all="true"
			:row-class-name="treeRowClass"
			class="cm-table pm-tree-table"
			style="width: 100%"
			cell-class-name="cm-cell"
			header-cell-class-name="cm-header">
			<el-table-column label="名称" min-width="180" show-overflow-tooltip>
				<template slot-scope="scope">
					<span class="cm-name" :class="{ 'is-top': Number(scope.row.pid) === parentId }">{{ scope.row.name || '—' }}</span>
				</template>
			</el-table-column>
			<el-table-column align="center" width="72" prop="weigh" label="排序" />
			<el-table-column align="center" width="132" label="操作">
				<template slot-scope="scope">
					<div class="cm-actions">
						<el-button v-if="canAdd" type="text" class="cm-link" @click="openCreate(scope.row.id)">子级</el-button>
						<el-button v-if="canEdit" type="text" icon="el-icon-edit" class="cm-link" @click="openEdit(scope.row.id)" />
						<el-popconfirm
							v-if="canDel"
							@onConfirm="delRow(scope.row.id)"
							confirmButtonType="danger"
							confirmButtonText="删除"
							title="确定删除？若有子分类或商品需先处理。">
							<el-button slot="reference" type="text" icon="el-icon-delete" class="cm-link is-danger" />
						</el-popconfirm>
					</div>
				</template>
			</el-table-column>
		</el-table>
		<p v-if="!loading && !treeList.length" class="cm-empty">该产品分类下还没有商品分类</p>

		<el-dialog
			:title="form.id ? '编辑商品分类' : (Number(form.pid) === parentId ? '添加商品分类' : '添加子分类')"
			:visible.sync="formVisible"
			width="480px"
			append-to-body
			custom-class="cm-form-dialog"
			@close="resetForm">
			<el-form ref="cateForm" :model="form" :rules="formRules" label-width="72px">
				<el-form-item label="名称" prop="name">
					<el-input v-model="form.name" placeholder="商品分类名称" />
				</el-form-item>
				<el-form-item label="上级">
					<el-select v-model="form.pid" style="width: 100%" :disabled="!!form.id">
						<el-option :value="parentId" :label="rootName ? (rootName + '（顶层）') : '当前产品分类（顶层）'" />
						<el-option
							v-for="p in parentSelectOptions"
							:key="p.id"
							:label="p.label"
							:value="p.id" />
					</el-select>
				</el-form-item>
				<el-form-item label="排序">
					<el-input-number v-model="form.weigh" :min="0" style="width: 100%" />
				</el-form-item>
			</el-form>
			<div slot="footer">
				<el-button @click="formVisible = false">取消</el-button>
				<el-button type="primary" :loading="saving" @click="saveForm">保存</el-button>
			</div>
		</el-dialog>
	</section>
</template>

<script>
export default {
	name: 'CategoryManage',
	props: {
		canAdd: { type: Boolean, default: false },
		canEdit: { type: Boolean, default: false },
		canDel: { type: Boolean, default: false },
		rootPid: { type: [Number, String], default: 0 },
		rootName: { type: String, default: '' },
	},
	data() {
		return {
			loading: false,
			saving: false,
			search: '',
			list: [],
			formVisible: false,
			form: this.emptyForm(0),
			formRules: { name: [{ required: true, message: '请输入名称', trigger: 'blur' }] },
		};
	},
	computed: {
		parentId() {
			return Number(this.rootPid) || 0;
		},
		scopedRows() {
			const rootId = this.parentId;
			if (!rootId) return [];
			const keep = {};
			let grew = true;
			this.list.forEach((r) => {
				if (Number(r.pid || 0) === rootId) keep[r.id] = true;
			});
			while (grew) {
				grew = false;
				this.list.forEach((r) => {
					if (keep[Number(r.pid || 0)] && !keep[r.id]) {
						keep[r.id] = true;
						grew = true;
					}
				});
			}
			return this.list.filter((r) => keep[r.id]);
		},
		visibleRows() {
			const q = String(this.search || '').trim().toLowerCase();
			const source = this.scopedRows;
			if (!q) return source;
			const matched = {};
			source.forEach((r) => {
				if (String(r.name || '').toLowerCase().indexOf(q) >= 0) {
					matched[r.id] = true;
					let pid = Number(r.pid || 0);
					while (pid && pid !== this.parentId) {
						matched[pid] = true;
						const parent = source.find((item) => Number(item.id) === pid);
						pid = parent ? Number(parent.pid || 0) : 0;
					}
				}
			});
			source.forEach((r) => {
				if (matched[Number(r.pid || 0)]) matched[r.id] = true;
			});
			return source.filter((r) => matched[r.id]);
		},
		treeList() {
			return buildGoodsCategoryTree(this.visibleRows, this.parentId);
		},
		parentSelectOptions() {
			return flattenForSelect(this.scopedRows, this.parentId);
		},
	},
	created() {
		this.getList();
	},
	watch: {
		rootPid() {
			this.getList();
		},
	},
	methods: {
		emptyForm(pid) {
			return { id: 0, name: '', weigh: 0, pid: pid || this.parentId };
		},
		api(action, params) {
			return this.$fetch('addons/hlm/api.FrontAdmin/' + action, Object.assign({ resource: 'category' }, params || {}));
		},
		post(action, params) {
			return this.$post('addons/hlm/api.FrontAdmin/' + action, Object.assign({ resource: 'category' }, params || {}));
		},
		getList() {
			const that = this;
			if (!that.parentId) {
				that.list = [];
				return;
			}
			that.loading = true;
			that.api('manageList', { page: 1, limit: 500 }).then(function(res) {
				that.list = (res.code === 1 && res.data && res.data.list) || [];
				that.$nextTick(function() { that.setExpandAll(true); });
			}).finally(function() {
				that.loading = false;
			});
		},
		onSearch() {
			this.$nextTick(() => this.setExpandAll(true));
		},
		treeRowClass({ row }) {
			const pid = Number(row.pid || 0);
			if (pid === this.parentId) return 'is-tree-root';
			const parent = findTreeNode(this.treeList, pid);
			const kids = parent && parent.children ? parent.children : [];
			const last = kids[kids.length - 1];
			return last && Number(last.id) === Number(row.id) ? 'is-tree-leaf is-tree-last' : 'is-tree-leaf';
		},
		setExpandAll(expand) {
			const table = this.$refs.cateTable;
			if (!table) return;
			const walk = function(nodes) {
				(nodes || []).forEach(function(row) {
					if (row.children && row.children.length) {
						table.toggleRowExpansion(row, expand);
						walk(row.children);
					}
				});
			};
			walk(this.treeList);
		},
		openCreate(pid) {
			if (!this.parentId) {
				this.$message.warning('请先选择产品分类');
				return;
			}
			this.form = this.emptyForm(Number(pid) || this.parentId);
			this.formVisible = true;
		},
		openEdit(id) {
			const that = this;
			that.api('manageDetail', { id }).then(function(res) {
				if (res.code !== 1) return;
				const row = res.data || {};
				that.form = {
					id: Number(row.id || 0),
					name: String(row.name || ''),
					weigh: Number(row.weigh || 0),
					pid: Number(row.pid || that.parentId),
				};
				that.formVisible = true;
			});
		},
		resetForm() {
			this.form = this.emptyForm(this.parentId);
		},
		saveForm() {
			const that = this;
			that.$refs.cateForm.validate(function(valid) {
				if (!valid) return;
				const pid = Number(that.form.pid) || that.parentId;
				if (!pid) {
					that.$message.warning('请选择上级分类');
					return;
				}
				that.saving = true;
				that.post('manageSave', {
					id: that.form.id,
					name: that.form.name,
					weigh: that.form.weigh,
					pid,
				}).then(function(res) {
					if (res.code === 1) {
						that.$message.success(res.msg || '保存成功');
						that.formVisible = false;
						that.getList();
						that.$emit('changed');
					}
				}).finally(function() {
					that.saving = false;
				});
			});
		},
		delRow(id) {
			const that = this;
			that.api('manageDel', { ids: String(id) }).then(function(res) {
				if (res.code === 1) {
					that.$message.success('已删除');
					that.getList();
					that.$emit('changed');
				}
			});
		},
	},
};

function buildGoodsCategoryTree(rows, rootPid) {
	const list = Array.isArray(rows) ? rows : [];
	const map = {};
	const roots = [];
	list.forEach((r) => {
		map[r.id] = Object.assign({}, r, { children: [] });
	});
	list.forEach((r) => {
		const node = map[r.id];
		const pid = Number(r.pid || 0);
		if (pid && pid !== Number(rootPid) && map[pid]) map[pid].children.push(node);
		else roots.push(node);
	});
	const prune = function(nodes) {
		nodes.forEach((n) => {
			if (n.children && n.children.length) prune(n.children);
			else delete n.children;
		});
	};
	prune(roots);
	return roots;
}

function flattenForSelect(rows, rootPid, pid, depth) {
	const curPid = pid == null ? Number(rootPid) : Number(pid);
	const kids = (rows || []).filter((r) => Number(r.pid || 0) === curPid);
	const out = [];
	kids.forEach((r) => {
		out.push({ id: r.id, label: `${depth ? '　'.repeat(depth) : ''}${r.name}` });
		out.push(...flattenForSelect(rows, rootPid, r.id, (depth || 0) + 1));
	});
	return out;
}

function findTreeNode(nodes, id) {
	const list = nodes || [];
	for (let i = 0; i < list.length; i++) {
		const n = list[i];
		if (Number(n.id) === Number(id)) return n;
		if (n.children && n.children.length) {
			const hit = findTreeNode(n.children, id);
			if (hit) return hit;
		}
	}
	return null;
}
</script>

<style scoped>
.category-manage {
	min-width: 0;
	overflow: hidden;
}
.cm-lead {
	margin: 0 0 12px;
	font-size: 13px;
	line-height: 1.5;
	color: var(--app-text-secondary, #6b7280);
}
.cm-lead strong { color: var(--app-text, #111827); }
.cm-toolbar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	margin-bottom: 12px;
	min-width: 0;
}
.cm-toolbar__ops {
	display: flex;
	align-items: center;
	gap: 6px;
	flex-shrink: 0;
}
.cm-search { flex: 1; min-width: 0; max-width: 200px; }
.cm-empty {
	margin: 20px 0 8px;
	text-align: center;
	font-size: 13px;
	color: #9ca3af;
}
.cm-table { width: 100% !important; }
.category-manage >>> .el-table,
.category-manage >>> .el-table__body-wrapper,
.category-manage >>> .el-table__header-wrapper {
	overflow-x: hidden !important;
}
.category-manage >>> .cm-header {
	background: #f9fafb !important;
	font-size: 13px;
	font-weight: 600;
}
.cm-name { font-size: 13px; }
.cm-name.is-top { font-weight: 600; }
.cm-actions {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 0;
}
.cm-link { color: var(--app-primary, #45b299); padding: 0 4px; }
.cm-link.is-danger { color: #ef4444; }
</style>
<style src="../../assets/css/front-admin.css"></style>
